/**
 * MapManager — base map and controls
 */
export default class MapManager {
    constructor(mapElement, block) {
        this.mapElement = mapElement
        this.block = block
        this.map = null
        this.userMarker = null
        this.markersUrls = []

        this.CONFIG = {
            LOCATE_USER: '[data-action="locate-user"]',
        }
    }

    /**
     * Initializes the map instance
     */
    async initialize() {
        if (!this.mapElement) {
            throw new Error("Map element not found")
        }

        // Wait until the map element is ready (style loading handled by IrisMap)
        const mapElement = this.mapElement

        if (typeof window.IrisMap === "undefined" || !mapElement) {
            return null
        }

        const mapId = parseInt(mapElement.getAttribute("id").replace("iris-map-", ""), 10)

        if (window.IrisMap.maps[mapId]) {
            this.map = window.IrisMap.maps[mapId]
            this.initLocateUserControl()
        }

        return this.map
    }

    /**
     * Returns the underlying map instance
     */
    getMap() {
        return this.map
    }

    /**
     * Initializes the geolocation control
     */
    initLocateUserControl() {
        const locateBtn = this.block?.querySelector(this.CONFIG.LOCATE_USER)
        if (!locateBtn) return

        locateBtn.addEventListener("click", () => {
            locateBtn.classList.add("is-loading")

            this.locateUser(
                () => locateBtn.classList.remove("is-loading"),
                () => {
                    locateBtn.classList.remove("is-loading")
                    alert("Impossible d'obtenir votre position. Veuillez vérifier vos paramètres de localisation.")
                }
            )
        })
    }

    /**
     * Centers the map on the user's position
     */
    locateUser(onSuccess, onError) {
        if (!navigator.geolocation) {
            console.warn("Geolocation is not supported")
            return
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const {latitude, longitude} = position.coords

                // Remove previous user marker
                if (this.userMarker) {
                    this.userMarker.remove()
                }

                // Custom DOM element for the marker
                const el = document.createElement("div")
                el.className = "user-location-marker"
                el.innerHTML = `
                    <div class="user-location-marker__pulse"></div>
                    <div class="user-location-marker__dot"></div>
                `

                // Add marker to the map
                this.userMarker = this.map
                    .Marker({element: el})
                    .setLngLat({lng: longitude, lat: latitude})
                    .addTo(this.map)

                this.map.flyTo({
                    center: [longitude, latitude],
                    zoom: 12,
                    duration: 1500,
                })

                if (onSuccess) onSuccess(position)
            },
            (error) => {
                console.error("Geolocation error:", error)
                if (onError) onError(error)
            },
            {
                enableHighAccuracy: false,
                timeout: 20000,
                maximumAge: 50000,
            }
        )
    }

    /**
     * Adds a marker cluster to the map
     *
     * @param {string} clusterId - Unique cluster identifier
     * @param {Array} markers - Markers to include in the cluster
     * @returns {Object|null} - Created cluster instance, or null on failure
     */
    addCluster(clusterId, markers) {
        if (!this.map) {
            console.error("MapManager: Map not initialized")
            return null
        }

        if (!markers || markers.length === 0) {
            console.warn(`MapManager: No markers provided for cluster ${clusterId}`)
            return null
        }

        try {
            // Remove any existing cluster with the same id
            this.removeCluster(clusterId)

            // Build cluster with IrisMap defaults + marker URLs
            const cluster = this.map.cluster(markers, {
                id: clusterId,
                imageUrl: this.markersUrls,
                circleOptions: {
                    radius: 12,
                    color: "#ffffff",
                    strokeColor: "#000000",
                    strokeWidth: 1.5,
                },
                popupOptions: window.IrisMap.config.main?.popupOptions || {},
            })

            return cluster
        } catch (error) {
            console.error(`MapManager: Error creating cluster ${clusterId}`, error)
            return null
        }
    }

    /**
     * Removes a marker cluster from the map
     *
     * @param {string} clusterId - Cluster id to remove
     */
    removeCluster(clusterId) {
        if (this.map.mapType !== "map-libre") {
            console.error("MapManager: Map not initialized")
            return
        }

        try {
            if (this.map.getLayer(`layer-${clusterId}`)) {
                this.map.removeLayer(`layer-${clusterId}`)
            }

            if (this.map.getLayer(`unclustered-point-${clusterId}`)) {
                this.map.removeLayer(`unclustered-point-${clusterId}`)
            }

            if (this.map.getLayer(`cluster-count-${clusterId}`)) {
                this.map.removeLayer(`cluster-count-${clusterId}`)
            }
            if (this.map.getSource(`source-${clusterId}`)) {
                this.map.removeSource(`source-${clusterId}`)
            }
        } catch (error) {
            console.warn(`MapManager: Error removing cluster ${clusterId}`, error)
        }
    }
}
